/**
 * Config-change audit API wrapper (v2 — Project Independence, DNKADM-0062;
 * re-points the v1 `/admin/config-audit` to `/v2/admin/config-audit`, handoff
 * §5.18, read-only).
 *
 * `GET /v2/admin/config-audit` is the paginated, newest-first history of per-tenant
 * CONFIGURATION writes — role-gated OWNER/ADMIN, tenant-from-JWT, bearer-only. It
 * backs the Configuration-group Config-change log (DNKADM-0024). It is the config
 * counterpart to the business Activity log (`/v2/admin/audit`, see `api/audit.ts`) —
 * the two NEVER double-report: member/admin business actions live in the Activity
 * audit; configuration writes (locations, services, resources, staff,
 * membership-plans, daily-caps, cancellation-bands, member-profile-fields,
 * payment-connections, tenant settings, …) live here. v2 widens the feed to ALL the
 * §5 config tables.
 *
 * CONTRACT (handoff §5.18) — the live endpoint returns the standard Spring `Page`
 * envelope `{ content, page, size, totalElements, totalPages }` (the rows live under
 * `content`). This wrapper still parses defensively — the legacy `entries`/`items`/
 * `results`/`data` row keys and a `total` count are tolerated as fallbacks — and is
 * tested against the live shape via MSW. Each entry (`v2_config_audit`) is
 * `{ actor, entityType, entityKey, action, oldValue, newValue, locationId, createdAt }`:
 *  - `actor`      — admin JWT sub/email (WHO performed the write).
 *  - `entityType` — one of the v2 config entity types (see {@link ConfigEntityType}).
 *  - `entityKey`  — the row identity within that type (e.g. a plan id, a tier|bucket).
 *  - `action`     — `create | update | delete` (unknown → carried raw, rendered as-is).
 *  - `oldValue`   — arbitrary object | null (null on create).
 *  - `newValue`   — arbitrary object | null (null on delete).
 *  - `locationId` — the owned location the write was scoped to, or null (tenant-wide).
 *  - `createdAt`  — ISO string (WHEN). (`timestamp` is tolerated as a legacy alias.)
 *
 * SECRETS: the server MASKS secret values to last-4 inside `oldValue`/`newValue`
 * (e.g. `access_token: "****wxyz"`); the raw secret is never returned. Non-secret
 * fields are returned in full. This client RENDERS the masked text VERBATIM — it
 * never unmasks, never edits, never re-fetches or treats any value as a live
 * secret, and does no client-side masking of its own (the server already masks).
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049): bearer
 * token, NO CSRF, NO cookies, the bounded 401→refresh→retry.
 *
 * DEFENSIVE PARSE — mirrors `api/audit.ts` / `api/members.ts`: each scalar field is
 * read from a small candidate-key list, a missing/wrong-typed field degrades to
 * `null` (never throws), `action` outside the known set is carried raw, the value
 * objects pass through as-is (arbitrary key/value records or null), and a non-array
 * payload yields an empty feed.
 */
import { request } from './client';
import { toWirePage, toUiPage } from './paging';

/**
 * The config entity types dink-api actually STORES (DNKADM-0392) — each string is
 * the `ENTITY_TYPE` / `AUDIT_ENTITY_TYPE` constant of one dink-api admin-service
 * writer (`ConfigAuditing.recordCreate/Update/Delete` callers), listed in a stable
 * display order for the type filter. The server's `?entityType=` filter is an exact
 * match on this stored string, so the filter must send it verbatim — the earlier
 * short names (`service`, `tier_credit`, …) matched nothing. An unrecognized
 * upstream type is still carried as a plain string (we never throw on an unknown
 * type; the screen labels it with a readable fallback).
 */
export const CONFIG_ENTITY_TYPES = [
  // Locations
  'v2_location',
  'v2_location_hours',
  'v2_location_hours_exception',
  // Services and pricing
  'v2_service',
  'v2_service_category',
  'v2_service_variation',
  'v2_service_category_assignment',
  'v2_service_location_assignment',
  'v2_service_staff_assignment',
  'v2_service_resource_assignment',
  'v2_variation_weekly_price',
  'v2_variation_date_price',
  'v2_staff_variation_price',
  // Resources
  'v2_resource',
  'v2_resource_location_assignment',
  'v2_resource_schedule',
  'v2_resource_date_exception',
  // Staff
  'v2_staff',
  'v2_staff_location_assignment',
  'v2_staff_schedule',
  'v2_staff_date_exception',
  'v2_staff_appointment_cap',
  'v2_staff_google_credential',
  // Memberships, offerings and credits
  'v2_membership_plan',
  'v2_membership_plan_credit',
  'v2_membership_plan_access_hours',
  'v2_membership_settings',
  'v2_cancellation_band',
  'v2_daily_cap',
  'v2_offering',
  'v2_offering_credit',
  'v2_credit_bucket',
  // Members, payments, tax, tenant
  'v2_member_profile_field',
  'v2_tenant_payment_method',
  'v2_tax_rate',
  'v2_tenant_settings',
  'v2_admin_caches',
] as const;

/** One of the stored entity types above. */
export type ConfigEntityType = (typeof CONFIG_ENTITY_TYPES)[number];

/** The constrained config action. An unrecognized value is carried raw (string). */
export type ConfigAction = 'create' | 'update' | 'delete';

/**
 * A config value snapshot (`oldValue` / `newValue`). The server returns an
 * arbitrary object of field → value (secrets already masked to last-4), or `null`
 * (null `oldValue` on create, null `newValue` on delete). Values are unknown
 * scalars/objects; the renderer stringifies them for display only.
 */
export type ConfigValue = Record<string, unknown>;

/**
 * One parsed config-audit entry. Every field may be `null` on the wire; `action`
 * is the normalized known action when recognized, else the raw string (rendered
 * as-is); `oldValue`/`newValue` are the (possibly secret-masked) snapshots or
 * `null`.
 */
export interface ConfigAuditEntry {
  /** WHO — the admin actor (JWT sub/email). */
  actor: string | null;
  /** WHEN — ISO `createdAt` as published; used for newest-first sort + display. */
  timestamp: string | null;
  /** The config entity type the write applied to. */
  entityType: ConfigEntityType | string | null;
  /** The row identity within that entity type (e.g. a plan id, `tier|bucket`). */
  entityKey: string | null;
  /** `create | update | delete`, or the raw string when unrecognized. */
  action: ConfigAction | string | null;
  /** The pre-write snapshot (null on create); secrets already masked server-side. */
  oldValue: ConfigValue | null;
  /** The post-write snapshot (null on delete); secrets already masked server-side. */
  newValue: ConfigValue | null;
  /** The owned location the write was scoped to (v2 §5.18), or null (tenant-wide). */
  locationId: string | null;
}

/** Parsed `GET /admin/config-audit` page. `total` is absent when upstream omits it. */
export interface ConfigAuditPage {
  entries: ConfigAuditEntry[];
  page: number;
  pageLimit: number;
  total: number | null;
}

// --- Defensive parsing helpers (mirror api/audit.ts) ---------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A finite number (accepts numeric strings), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** First candidate key that yields a non-null trimmed string, else null. */
function pickText(o: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const k of keys) {
    const v = asText(o[k]);
    if (v !== null) return v;
  }
  return null;
}

const KNOWN_ACTIONS: ReadonlySet<string> = new Set(['create', 'update', 'delete']);

/**
 * Normalize a raw action: a known action (`create | update | delete`) passes
 * through lower-cased; an unrecognized value is carried RAW (rendered as-is by
 * the screen), and an absent/blank value degrades to null.
 */
function parseAction(raw: unknown): ConfigAction | string | null {
  const text = asText(raw);
  if (text === null) return null;
  const a = text.toLowerCase();
  return KNOWN_ACTIONS.has(a) ? (a as ConfigAction) : text;
}

/**
 * A value snapshot: an object of field → value passes through as-is (secrets
 * already masked server-side); anything else (absent, null, a primitive, an
 * array) degrades to null. The values are intentionally NOT coerced — the
 * renderer stringifies them for display only.
 */
function parseValue(raw: unknown): ConfigValue | null {
  return isObject(raw) ? raw : null;
}

function parseEntry(raw: unknown): ConfigAuditEntry {
  const o = isObject(raw) ? raw : {};
  return {
    actor: pickText(o, ['actor', 'actorEmail', 'performedBy', 'user']),
    // v2 names the timestamp `createdAt`; `timestamp` is tolerated as a legacy alias.
    timestamp: pickText(o, ['createdAt', 'timestamp', 'occurredAt', 'time']),
    entityType: pickText(o, ['entityType', 'entity_type', 'type']),
    entityKey: pickText(o, ['entityKey', 'entity_key', 'key', 'id']),
    action: parseAction(o['action'] ?? o['event']),
    oldValue: parseValue(o['oldValue'] ?? o['old_value'] ?? o['before']),
    newValue: parseValue(o['newValue'] ?? o['new_value'] ?? o['after']),
    locationId: pickText(o, ['locationId', 'location_id']),
  };
}

/**
 * Parse the entries array defensively. The live Spring `Page` publishes the rows
 * under `content` (checked first); the legacy `entries`/`items`/`results`/`data`
 * names are tolerated as fallbacks. A non-array (or missing) field yields an empty
 * list rather than throwing — the same "non-array payload is empty" contract as
 * `api/audit.ts`.
 */
function parseEntries(body: Record<string, unknown>): ConfigAuditEntry[] {
  const candidates = [
    body['content'],
    body['entries'],
    body['items'],
    body['results'],
    body['data'],
  ];
  const arr =
    candidates.find((c): c is unknown[] => Array.isArray(c)) ??
    (Array.isArray(body) ? (body as unknown[]) : null);
  if (!arr) return [];
  return arr.map(parseEntry);
}

/** Parse an epoch-or-ISO timestamp to a sortable number; unknown → -Infinity (sorts last). */
function timestampOrder(ts: string | null): number {
  if (ts === null) return Number.NEGATIVE_INFINITY;
  const ms = Date.parse(ts);
  if (Number.isFinite(ms)) return ms;
  const num = Number(ts);
  return Number.isFinite(num) ? num : Number.NEGATIVE_INFINITY;
}

/**
 * Re-sort newest-first by timestamp. The endpoint is documented as already
 * newest-first, but we re-sort client-side defensively so a server that returns
 * a different order still renders correctly. Entries with no parseable timestamp
 * sort to the end, preserving their relative order (stable).
 */
function sortNewestFirst(entries: ConfigAuditEntry[]): ConfigAuditEntry[] {
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const diff = timestampOrder(b.e.timestamp) - timestampOrder(a.e.timestamp);
      return diff !== 0 ? diff : a.i - b.i;
    })
    .map(({ e }) => e);
}

function parsePage(
  body: unknown,
  fallbackPage: number,
  fallbackLimit: number,
): ConfigAuditPage {
  const o = isObject(body) ? body : {};
  return {
    entries: sortNewestFirst(parseEntries(o)),
    page: asNumber(o['page']) ?? fallbackPage,
    // v2 publishes the page size as `size`; `pageLimit` is tolerated as a legacy alias.
    pageLimit: asNumber(o['size']) ?? asNumber(o['pageLimit']) ?? fallbackLimit,
    // The live Spring `Page` publishes the count as `totalElements`; `total` is a legacy fallback.
    total: asNumber(o['totalElements']) ?? asNumber(o['total']),
  };
}

// --- Endpoints -----------------------------------------------------------------

export interface ConfigAuditParams {
  page?: number;
  /** Page size (handoff §5.18 `size`); defaults to 20. */
  size?: number;
  /** Server-side entity-type filter; sent as `?entityType=` ONLY when set. */
  entityType?: ConfigEntityType | string;
  /** Server-side actor filter; sent as `?actor=` ONLY when set. */
  actor?: string;
  /**
   * Inclusive date-range lower bound, an ISO date (`yyyy-MM-dd`); sent as
   * `?fromDate=` ONLY when set. The server binds `fromDate`/`toDate` — the earlier
   * `from`/`to` names were ignored (DNKADM-0392).
   */
  fromDate?: string;
  /** Inclusive date-range upper bound, an ISO date; sent as `?toDate=` ONLY when set. */
  toDate?: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 20;

/**
 * `GET /v2/admin/config-audit?page&size[&entityType=&actor=&fromDate=&toDate=]` → a parsed,
 * newest-first page of the config-change feed (handoff §5.18). Defaults `page=1`,
 * `size=20`. Each filter is sent ONLY when set; omitting `entityType` returns all
 * entity types. Bearer-only `/v2/admin/**` surface (no cookies, no CSRF — handled by
 * `request`); the bounded 401→refresh→retry applies. Throws `ApiError` on a non-2xx
 * response (the caller renders the 403 "needs OWNER/ADMIN" state).
 */
export async function listConfigAudit(
  params: ConfigAuditParams = {},
): Promise<ConfigAuditPage> {
  const page = params.page ?? DEFAULT_PAGE;
  const size = params.size ?? DEFAULT_PAGE_LIMIT;
  const entityType = asText(params.entityType) ?? undefined;
  const actor = asText(params.actor) ?? undefined;
  const fromDate = asText(params.fromDate) ?? undefined;
  const toDate = asText(params.toDate) ?? undefined;
  // The UI keeps a 1-indexed page, but `/v2/admin/config-audit` is 0-indexed. Send
  // `toWirePage(page)` so the first UI page (1) maps to `?page=0` — sending `?page=1`
  // raw would skip past 0-indexed page 0 (latent off-by-one, DNKADM-0148).
  const body = await request<unknown>('GET', '/v2/admin/config-audit', {
    query: { page: toWirePage(page), size, entityType, actor, fromDate, toDate },
  });
  // `parsePage` returns the backend's raw 0-indexed page; remap it back to the
  // 1-indexed UI page HERE (mirrors listMembers / listActivityAudit). The 0-indexed
  // fallback makes `toUiPage` round-trip the requested page when `page` is omitted.
  const parsed = parsePage(body, toWirePage(page), size);
  return { ...parsed, page: toUiPage(parsed.page) };
}
